import { useState, type CSSProperties } from 'react';

// 일정 시간 고르기(2026-10-20 오너 — "10분 단위 시간만 나오게", "01시 02시 말고 1시 2시").
// 브라우저 기본 <input type="time"> 은 step 을 줘도 분 목록에 1분 단위가 다 나오고 시 앞에 0 이 붙는다
// → 고르기 칸으로 바꿨다. 값은 그대로 'HH:MM'(24시간, 빈 값 = 시간 없음).
//
// 2026-10-22 오너 — "오전, 오후 여부 선택하고 1시~12시까지 보이게" → 세 칸:
//   [선택 · 오전 · 오후] [1시~12시] [00분~50분]. 오전 12시 = 00시, 오후 12시 = 12시(정오).
//   오전/오후만 고르고 시를 아직 안 골랐으면 값은 비운 채(시간 없음) 시 칸만 연다.

type Meridiem = '' | 'am' | 'pm';

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = ['00', '10', '20', '30', '40', '50'];

/** 24시간 시(0~23) → 오전/오후 + 1~12시. */
export function toMeridiem(h24: number): { meridiem: 'am' | 'pm'; hour12: number } {
  return { meridiem: h24 < 12 ? 'am' : 'pm', hour12: h24 % 12 === 0 ? 12 : h24 % 12 };
}

/** 오전/오후 + 1~12시 → 24시간 시(0~23). */
export function toHour24(meridiem: 'am' | 'pm', hour12: number): number {
  return (hour12 % 12) + (meridiem === 'pm' ? 12 : 0);
}

/** 'HH:MM' → '오후 2시 30분'(정각이면 '오후 2시'). 빈 값은 ''. */
export function formatTime12(value: string): string {
  const [hh, mm] = value.split(':');
  if (!hh) return '';
  const { meridiem, hour12 } = toMeridiem(Number(hh));
  const min = mm && mm !== '00' ? ` ${mm}분` : '';
  return `${meridiem === 'am' ? '오전' : '오후'} ${hour12}시${min}`;
}

export function TimeSelect({
  value,
  onChange,
  style,
  testId,
  ariaLabel = '시간',
}: {
  value: string;
  onChange: (next: string) => void;
  style?: CSSProperties;
  testId?: string;
  ariaLabel?: string;
}) {
  const [hh = '', mm = ''] = value ? value.split(':') : [];
  const parsed = hh === '' ? null : toMeridiem(Number(hh));
  // 오전/오후만 고른 상태(값은 아직 비어 있음).
  const [pending, setPending] = useState<Meridiem>('');
  const meridiem: Meridiem = parsed?.meridiem ?? pending;
  // 예전에 10분 단위가 아닌 값으로 저장된 일정(예: 14:05)은 그 값도 목록에 남겨 그대로 보인다.
  const minutes = mm && !MINUTES.includes(mm) ? [...MINUTES, mm].sort() : MINUTES;
  const pad = (n: string | number) => String(n).padStart(2, '0');
  const emit = (m: 'am' | 'pm', hour12: number, minute: string) =>
    onChange(`${pad(toHour24(m, hour12))}:${minute || '00'}`);

  const onMeridiem = (next: Meridiem) => {
    setPending(next);
    if (next === '') onChange('');
    else if (parsed) emit(next, parsed.hour12, mm);
  };

  const sel = (extra: CSSProperties, off = false): CSSProperties => ({
    ...style,
    flex: 1,
    minWidth: 0,
    ...extra,
    ...(off ? { opacity: 0.5 } : {}),
  });

  return (
    <div style={{ display: 'flex', gap: 6, flex: 1, minWidth: 0 }}>
      <select
        value={meridiem}
        onChange={(e) => onMeridiem(e.target.value as Meridiem)}
        style={sel({ flex: 1.3 })}
        aria-label={`${ariaLabel}, 오전 또는 오후`}
        data-testid={testId ? `${testId}-meridiem` : undefined}
      >
        <option value="">선택</option>
        <option value="am">오전</option>
        <option value="pm">오후</option>
      </select>
      <select
        value={parsed ? String(parsed.hour12) : ''}
        onChange={(e) => {
          if (meridiem === '' || e.target.value === '') return;
          emit(meridiem, Number(e.target.value), mm);
        }}
        disabled={meridiem === ''}
        style={sel({}, meridiem === '')}
        aria-label={`${ariaLabel}, 시`}
        data-testid={testId ? `${testId}-hour` : undefined}
      >
        {!parsed && <option value="">시</option>}
        {HOURS_12.map((h) => (
          <option key={h} value={String(h)}>
            {h}시
          </option>
        ))}
      </select>
      <select
        value={mm}
        onChange={(e) => parsed && emit(parsed.meridiem, parsed.hour12, e.target.value)}
        disabled={!parsed}
        style={sel({}, !parsed)}
        aria-label={`${ariaLabel}, 분`}
        data-testid={testId ? `${testId}-minute` : undefined}
      >
        {!parsed && <option value="">분</option>}
        {minutes.map((m) => (
          <option key={m} value={m}>
            {m}분
          </option>
        ))}
      </select>
    </div>
  );
}
